import { useEffect } from 'react'

import { isBoolean } from 'lodash-es'
import { useTranslation } from 'react-i18next'

import { Button } from '@/Atomic/Molecules/Button'
import { MenuItem, MenuItemProps } from '@/Atomic/Molecules/MenuItem'
import { DropdownMenu } from '@/Atomic/Organisms/DropdownMenu'
import { MenuSheet } from '@/Atomic/Organisms/MenuSheet'
import { useIconMenu } from '@/hooks/useIconMenu'
import { useMediaQuery } from '@/hooks/useMediaQuery'

interface TicketActionMenuProps {
  menuItems: MenuItemProps[]
  onOpenChange: (isOpen: boolean) => void
  /**
   * Toggle this value to open the menu from outside, e.g. on a right click on
   * the row. Keep it `undefined` until the first external trigger, otherwise
   * the menu would already open on mount.
   */
  trigger?: boolean
}

export const TicketActionMenu: React.FC<TicketActionMenuProps> = ({
  menuItems,
  onOpenChange,
  trigger,
}) => {
  const { t } = useTranslation()
  const isMobile = useMediaQuery('(max-width: 766px)')
  const { buttonRef, menuIsOpen, setMenuIsOpen, toggleMenu } = useIconMenu()

  useEffect(() => {
    if (isMobile) {
      onOpenChange(menuIsOpen)
    }
  }, [isMobile, menuIsOpen, onOpenChange])

  useEffect(() => {
    if (isMobile && isBoolean(trigger)) {
      toggleMenu()
    }
  }, [isMobile, trigger, toggleMenu])

  if (!isMobile) {
    return (
      <DropdownMenu
        boundarySelector=".scrollable-panel__body"
        buttonClassName="borderless"
        menuItems={menuItems}
        onOpenChange={onOpenChange}
        trigger={trigger}
      />
    )
  }

  return (
    <div ref={buttonRef} className="ticket-action-menu">
      <Button
        icon="more-horizontal"
        variant="icon"
        size="sm"
        tooltip={t('tooltip.more')}
        buttonClassName="o-menu-button borderless"
        onClick={(event) => {
          event.preventDefault()
          event.stopPropagation()
          toggleMenu()
        }}
      />
      <MenuSheet
        isOpen={menuIsOpen}
        onBackdropClick={(event) => {
          event.preventDefault()
          event.stopPropagation()
          setMenuIsOpen(false)
        }}
        onMobileCloseClick={(event) => {
          event.preventDefault()
          event.stopPropagation()
          setMenuIsOpen(false)
        }}
      >
        <div className="ticket-action-menu__list">
          {menuItems.map((item, index) => (
            <MenuItem
              key={`${item.label}-${index}`}
              {...item}
              onClick={(event) => {
                item.onClick?.(event)
                setMenuIsOpen(false)
              }}
            />
          ))}
        </div>
      </MenuSheet>
    </div>
  )
}
